import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import ICalendar from '../../_interfaces/ICalendar';
import IJob from '../../_interfaces/IJob';
import IMarketingChannels from '../../_interfaces/IMarketingChannels';
import IPatient from '../../_interfaces/IPatients';
import IProduct from '../../_interfaces/IProduct';
import IProductCategory from '../../_interfaces/IProductCategory';
import IRole from '../../_interfaces/IRole';
import ISite from '../../_interfaces/ISite';
import ITreatment from '../../_interfaces/ITreatment';
import ITreatmentCategory from '../../_interfaces/ITreatmentCategory';
import IUser from '../../_interfaces/IUser';
import { CalendarService } from '../calendars/calendar.service';
import { CustomersService } from '../customers/customers.service';
import { JobsService } from '../jobs/jobs.service';
import { MarketingChannelsService } from '../marketing-channels/marketing-channels.service';
import { PatientsService } from '../patients/patients.service';
import { ProductsCategoriesService } from '../products/products-categories.service';
import { ProductsService } from '../products/products.service';
import { RolesService } from '../roles/roles.service';
import { SitesService } from '../sites/sites.service';
import { TreatmentsCategoriesService } from '../treatments/treatments-categories.service';
import { TreatmentsService } from '../treatments/treatments.service';
import { UsersService } from '../users/users.service';

@Injectable({
  providedIn: 'root',
})
export class AdminEntityLoaderService {
  constructor(
    private readonly usersService: UsersService,
    private readonly rolesService: RolesService,
    private readonly sitesService: SitesService,
    private readonly patientsService: PatientsService,
    private readonly treatmentsService: TreatmentsService,
    private readonly productsService: ProductsService,
    private readonly jobsService: JobsService,
    private readonly marketingService: MarketingChannelsService,
    private readonly treatmentCategoriesService: TreatmentsCategoriesService,
    private readonly productCategoriesService: ProductsCategoriesService,
    private readonly calendarsService: CalendarService,
    private readonly customersService: CustomersService,
  ) {}

  loadUser(customersId: number, usersId: number): Observable<IUser | null> {
    return this.usersService.get(customersId, usersId).pipe(
      catchError(() =>
        this.customersService.getUsers(customersId).pipe(
          map((users) => users.find((item) => +item.id === +usersId) ?? null),
          catchError(() => of(null)),
        ),
      ),
    );
  }

  loadRole(customersId: number, rolesId: number): Observable<IRole | null> {
    return this.rolesService.get(customersId, rolesId).pipe(
      catchError(() =>
        this.rolesService.getByCustomer(customersId).pipe(
          map((roles) => roles.find((item) => +item.id === +rolesId) ?? null),
          catchError(() => of(null)),
        ),
      ),
    );
  }

  loadClinic(customersId: number, sitesId: number): Observable<ISite | null> {
    return this.sitesService.getSummary(customersId, sitesId).pipe(
      catchError(() =>
        this.customersService.getSites(customersId).pipe(
          map((sites) => sites.find((item) => +item.id === +sitesId) ?? null),
          catchError(() => of(null)),
        ),
      ),
    );
  }

  loadPatient(
    customersId: number,
    patientsId: number,
  ): Observable<IPatient | null> {
    return this.patientsService.get(customersId, patientsId).pipe(
      catchError(() =>
        this.patientsService.getAll(customersId).pipe(
          map(
            (patients) =>
              patients.find((item) => +item.id === +patientsId) ?? null,
          ),
          catchError(() => of(null)),
        ),
      ),
    );
  }

  loadTreatment(
    customersId: number,
    treatmentsId: number,
  ): Observable<ITreatment | null> {
    return this.treatmentsService.getAll(customersId).pipe(
      map(
        (treatments) =>
          treatments.find((item) => +item.id === +treatmentsId) ?? null,
      ),
      catchError(() => of(null)),
    );
  }

  loadProduct(
    customersId: number,
    productsId: number,
  ): Observable<IProduct | null> {
    return this.productsService.getAllByCustomer(customersId).pipe(
      map((products) => products.find((item) => +item.id === +productsId) ?? null),
      catchError(() => of(null)),
    );
  }

  loadJob(customersId: number, jobsId: number): Observable<IJob | null> {
    return this.jobsService.getJobs(customersId).pipe(
      map((jobs) => jobs.find((item) => +item.id === +jobsId) ?? null),
      catchError(() => of(null)),
    );
  }

  loadMarketingChannel(
    customersId: number,
    channelsId: number,
  ): Observable<IMarketingChannels | null> {
    return this.marketingService.getAll(customersId).pipe(
      map(
        (channels) => channels.find((item) => +item.id === +channelsId) ?? null,
      ),
      catchError(() => of(null)),
    );
  }

  loadTreatmentCategory(
    customersId: number,
    categoryId: number,
  ): Observable<ITreatmentCategory | null> {
    return this.treatmentCategoriesService.getAll(customersId).pipe(
      map(
        (categories) =>
          categories.find((item) => +item.id === +categoryId) ?? null,
      ),
      catchError(() => of(null)),
    );
  }

  loadProductCategory(
    customersId: number,
    categoryId: number,
  ): Observable<IProductCategory | null> {
    return this.productCategoriesService.getByCustomer(customersId).pipe(
      map(
        (categories) =>
          categories.find((item) => +item.id === +categoryId) ?? null,
      ),
      catchError(() => of(null)),
    );
  }

  loadCalendar(
    customersId: number,
    sitesId: number,
    calendarId: number,
  ): Observable<ICalendar | null> {
    return this.calendarsService.get(customersId, { id: sitesId } as ISite).pipe(
      map(
        (calendars) =>
          calendars.find((item) => +item.id === +calendarId) ?? null,
      ),
      catchError(() => of(null)),
    );
  }
}
